import React from "react";
import { Badge, Table } from "reactstrap";
import moment from "moment";

// Change #100747 - shared bits for the HMRC VAT and Corporation Tax filing screens.

// HMRC fraud prevention headers (WEB_APP_VIA_SERVER) need facts only the browser knows.
// They go to our API as ClientInfo and the server turns them into Gov-Client-* headers.
const DEVICE_KEY = "crmit_hmrc_device_id";

const deviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (e) {
    return undefined;
  }
};

const utcOffset = () => {
  const mins = -new Date().getTimezoneOffset();
  const sign = mins >= 0 ? "+" : "-";
  const abs = Math.abs(mins);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

export const clientInfo = () => ({
  DeviceId: deviceId(),
  Screens: [
    {
      Width: window.screen?.width,
      Height: window.screen?.height,
      ScalingFactor: window.devicePixelRatio || 1,
      ColourDepth: window.screen?.colorDepth || 24,
    },
  ],
  WindowWidth: window.innerWidth,
  WindowHeight: window.innerHeight,
  Timezone: utcOffset(),
  UserAgent: navigator.userAgent,
  DoNotTrack: navigator.doNotTrack === "1" || window.doNotTrack === "1",
});

export const STATUS_COLORS = {
  ACCEPTED: "success",
  SUBMITTED: "info",
  REJECTED: "danger",
  ERROR: "danger",
};

export const STATUS_LABELS = {
  ACCEPTED: "Accepted",
  SUBMITTED: "Awaiting HMRC",
  REJECTED: "Rejected",
  ERROR: "Error",
};

export const fmtDate = (d, f = "DD MMM YYYY") => (d ? moment(d).format(f) : "-");

export const fmtMoney = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-";
};

export const differs = (a, b) => Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) >= 0.005;

export function EnvBadge({ env }) {
  return <Badge color={env === "live" ? "success" : "secondary"}>{env === "live" ? "Live" : "Test"}</Badge>;
}

export function StatusBadge({ code }) {
  return (
    <Badge color={STATUS_COLORS[code] || "secondary"} className="me-1">
      {STATUS_LABELS[code] || code}
    </Badge>
  );
}

// What CRMit worked out against what was sent, for every overtyped figure.
export function OvertypeTable({ overtypes }) {
  if (!overtypes || overtypes.length === 0) {
    return <p className="text-muted small mb-2">No figures were overtyped - everything sent matches CRMit's calculation.</p>;
  }
  return (
    <div className="table-responsive">
      <Table size="sm" className="align-middle mb-2">
        <thead>
          <tr>
            <th>Overtyped figure</th>
            <th className="text-end">CRMit calculated</th>
            <th className="text-end">Submitted</th>
            <th className="text-end">Difference</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {overtypes.map((o) => (
            <tr key={o.Field}>
              <td>
                {o.Label}
                {o.Derived && <span className="text-muted small"> (follows from other figures)</span>}
              </td>
              <td className="text-end">{fmtMoney(o.Calculated)}</td>
              <td className="text-end fw-semibold">{fmtMoney(o.Submitted)}</td>
              <td className={`text-end ${o.Difference < 0 ? "text-danger" : "text-success"}`}>{fmtMoney(o.Difference)}</td>
              <td className="small">{o.Reason || <span className="text-muted">-</span>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function IxbrlFrame({ html, height = 420, title = "Document preview" }) {
  // sandbox="" - no scripts, no navigation; uploaded iXBRL is content, not code.
  return (
    <iframe
      title={title}
      sandbox=""
      srcDoc={html || ""}
      style={{ width: "100%", height, border: "1px solid var(--bs-border-color)", borderRadius: 6, background: "#fff" }}
    />
  );
}

export function CodeBlock({ text, maxHeight = 320 }) {
  return (
    <pre
      className="small p-2 mb-0"
      style={{ maxHeight, overflow: "auto", background: "var(--bs-tertiary-bg)", borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-all" }}
    >
      {text}
    </pre>
  );
}

export const prettyJson = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  try {
    return JSON.stringify(typeof raw === "string" ? JSON.parse(raw) : raw, null, 2);
  } catch (e) {
    return String(raw);
  }
};

// Line breaks between tags so a GovTalk message is readable in the preview.
export const prettyXml = (xml) => String(xml || "").replace(/></g, ">\n<");
